import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';

import {
  bridgeAtom,
  chatFeedbackAtom,
  errorAtom,
  isLoadingMessageAtom,
  isReadyForScrollAtom,
  messagesAtom,
  renderedMessagesAtom,
  useAtom,
  useValue,
} from '@/store';

import { isHebrew } from '@/utils';

import { ErrorMessage, RefreshErrorTypes } from '@/modules/common';
import { MessageReasonType, MessageSenderType, MessageType, SendToModelStatusType } from '@/modules/models';

import { BubbleLoading, MessageText, MultiChoice, SingleChoice } from './components';
import { ChoiceType } from './data';
import {
  MessageContainer,
  MessagesContainer,
  RemoveMessageIcon,
  RemoveMessageIconWrapper,
  WarningDescription,
  WarningDescriptionIcon,
} from './Messages.styles';

export interface MessagesProps {
  resendMessage: (msg: MessageType) => void;
  sendMessage: (msg?: string) => void;
  removeMessage: (msg: MessageType) => void;
}

export const Messages = memo(
  forwardRef<HTMLUListElement, MessagesProps>((props, ref) => {
    const { resendMessage, sendMessage, removeMessage } = props;

    const [error, setError] = useAtom(errorAtom);
    const history = useValue(messagesAtom);
    const { translations, scrollToUserMessage } = useValue(bridgeAtom);
    const chatFeedback = useValue(chatFeedbackAtom);
    const isLoadingMessage = useValue(isLoadingMessageAtom);
    const isReadyForScroll = useValue(isReadyForScrollAtom);
    const renderedMessages = useValue(renderedMessagesAtom);
    const isShowRefreshButton = useMemo(() => RefreshErrorTypes.includes(error!), [error]);

    const messagesRef = useRef<HTMLUListElement | null>(null);
    const scrolledMessagesRef = useRef<number[]>([]);
    useImperativeHandle(ref, () => messagesRef.current as HTMLUListElement);

    const onClickRemoveMessage = useCallback(
      (msg: MessageType) => (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();

        removeMessage(msg);
      },
      [removeMessage]
    );

    const onClickReload = useCallback(() => {
      setError(null);
    }, [setError]);

    // need to reverse the history to show the messages in the correct order for mobile chat when opening keyboard
    const messages = useMemo(() => [...history].reverse(), [history]);

    const [adjustedIndex, adjustedMlIndex] = useMemo(() => {
      const lastUserMessageIndex = messages.findIndex((msg) => msg.role === MessageSenderType.USER);
      const lastMlMessageIndex = messages.findIndex((msg) => msg.role === MessageSenderType.ASSISTANT);

      const adjustedIndex = lastUserMessageIndex !== -1 ? messages.length - 1 - lastUserMessageIndex : -1;
      const adjustedMlIndex = lastMlMessageIndex !== -1 ? messages.length - 1 - lastMlMessageIndex : -1;

      return [adjustedIndex, adjustedMlIndex];
    }, [messages]);

    const lastUserMessage = useRef<null | HTMLLIElement>(null);
    const lastMlMessage = useRef<null | HTMLLIElement>(null);

    useEffect(() => {
      const lastMessage = history[history.length - 1];
      const isLastMessageFromML =
        lastMessage?.role === MessageSenderType.ASSISTANT && lastMessage?.reason === MessageReasonType.MODEL_RESPONSE;
      const isLastMessageFromUser = lastMessage?.role === MessageSenderType.USER;

      const isAllRendered = Object.values(renderedMessages).filter(Boolean);

      if (
        history.length === 0 ||
        !lastUserMessage.current ||
        !lastMlMessage.current ||
        !messagesRef.current ||
        !isReadyForScroll ||
        !isAllRendered
      ) {
        return;
      }

      const messagesContainerHeight = messagesRef?.current?.clientHeight || 0;
      const lastMlMessageHeight = lastMlMessage.current?.scrollHeight || 0;

      const containerRect = messagesRef.current?.getBoundingClientRect();
      const messageRect = lastUserMessage.current?.getBoundingClientRect();

      const isFullyVisible = messageRect.top >= containerRect.top && messageRect.bottom <= containerRect.bottom;

      if (lastMessage.id && !scrolledMessagesRef.current.includes(lastMessage.id)) {
        scrolledMessagesRef.current.push(lastMessage.id);

        switch (true) {
          case isLastMessageFromUser:
            return lastUserMessage.current.scrollIntoView({ behavior: 'smooth' });

          case isLastMessageFromML && !isFullyVisible && scrollToUserMessage:
            setTimeout(() => {
              lastUserMessage.current?.scrollIntoView({ behavior: 'instant' });
            }, 25);
            break;

          case isLastMessageFromML && lastMlMessageHeight <= messagesContainerHeight:
            return messagesRef.current?.scrollTo({
              top: messagesRef.current.scrollHeight,
              behavior: 'smooth',
            });
        }
      }
    }, [renderedMessages, history, isReadyForScroll]);

    const getRef = useCallback(
      (index: number) => {
        switch (index) {
          case adjustedIndex:
            return lastUserMessage;

          case adjustedMlIndex:
            return lastMlMessage;

          default:
            return null;
        }
      },
      [adjustedIndex, adjustedMlIndex]
    );

    // remember to reverse the array back when rendering
    return (
      <MessagesContainer hasChatFeedback={Boolean(chatFeedback)} ref={messagesRef}>
        {error && (
          <ErrorMessage
            isShowRefreshButton={isShowRefreshButton}
            message={
              error ? (window.LLL(translations.errors[error]) ?? window.LLL(translations.errors.default)) : undefined
            }
            onClick={onClickReload}
          />
        )}
        {isLoadingMessage && !error && <BubbleLoading />}

        {messages.map((msg, index) => {
          const key = msg.replyMessageId ? `${msg.replyMessageId}${msg.id}` : (msg.clientCorrelationId ?? msg.id);

          const hasFailed = msg.status === SendToModelStatusType.FAILED;

          const isLastMessage = index === 0; // because we reversed the array

          const isShowChoice =
            isLastMessage && msg.role === MessageSenderType.ASSISTANT && (msg.options?.length ?? 0) > 0;

          let Choice: React.ComponentType<ChoiceType> | null = null;

          if (isShowChoice) {
            Choice = msg.multiChoice ? MultiChoice : SingleChoice;
          }

          const ref = getRef(messages.length - 1 - index);
          const isUserMessage = msg.role === MessageSenderType.USER;

          const failedMessageProps = hasFailed
            ? {
                hasFailed,
                role: 'button',
                ariaLabel: 'resend button',
                onClick: () => resendMessage(msg),
              }
            : {};

          const isHebrewMsg = isHebrew(msg.content);
          const content = msg.contentNew || msg.content;

          return (
            <MessageContainer key={key} ref={ref} isUserMessage={isUserMessage} {...failedMessageProps}>
              {!Choice && (
                <MessageText
                  id={msg.id}
                  links={msg.links}
                  isUserMessage={isUserMessage}
                  text={content}
                  mlBeProcessingTime={msg.mlBeProcessingTimeMs}
                  direction={isHebrewMsg ? 'rtl' : 'ltr'}
                />
              )}

              {Choice && <Choice disabled={isLoadingMessage} options={msg.options!} sendMessage={sendMessage} />}

              {hasFailed && (
                <>
                  <RemoveMessageIconWrapper
                    role="button"
                    aria-label="remove button"
                    onClick={onClickRemoveMessage(msg)}
                  >
                    <RemoveMessageIcon />
                  </RemoveMessageIconWrapper>

                  <WarningDescription role="button" aria-label="resend button">
                    <WarningDescriptionIcon />
                    {window.LLL(translations.failedMessage)}
                  </WarningDescription>
                </>
              )}
            </MessageContainer>
          );
        })}
      </MessagesContainer>
    );
  })
);

Messages.displayName = 'Messages';
